import { useState } from "react";
import { Loader2 } from "lucide-react";
import ArtifactReviewList from "@/components/evidenceGathering/ArtifactReviewList";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { ErrorBanner } from "@/components/ingestion";
import { useConfirmArtifacts } from "@/hooks/useProjects";
import { recordArtefactEvents } from "@/lib/evidenceAudit";
import { reviewProgress } from "@/lib/artifacts";
import type { Artifact } from "@/services/projectsApi";

interface ArtifactReviewDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  artifacts: Artifact[];
  projectId: string;
  /** The workbook these artefacts came from. Kept so a failed row can be
   *  re-extracted without starting the upload again. */
  sourceFile: File;
  /** The project's highest upload version, for the version pills. */
  latestUploadVersion?: number;
}

/** The second step of the upload: the descriptions a model wrote, laid out for
 *  a person to check before they finish.
 *
 *  Presents as the same "Upload CEM Model" dialog the selection step used —
 *  same title, same size — because to the user this is one modal that moved on
 *  a step, not a new window. It is a separate component only because
 *  UploadCemModel is unmounted by its own success: on a project's first upload
 *  that component lives inside the empty state, and the arriving artefacts
 *  replace the empty state with the grid. A review step living there would be
 *  destroyed the moment it had something to show.
 *
 *  Reviewing is not a gate. The artefacts are on the project already, so
 *  Continue only closes, and anything skipped can be read from its card. */
const ArtifactReviewDialog = ({
  open,
  onOpenChange,
  artifacts,
  projectId,
  sourceFile,
  latestUploadVersion = 0,
}: ArtifactReviewDialogProps) => {
  const [retryingUids, setRetryingUids] = useState<Set<string>>(new Set());
  const retryArtifact = useConfirmArtifacts();
  const progress = reviewProgress(artifacts);

  // Re-extracting one artefact is the retry: confirm overwrites every field for
  // a uid that already exists, status and error included, so the row goes back
  // through the queue.
  const handleRetry = (artifact: Artifact) => {
    setRetryingUids((prev) => new Set(prev).add(artifact.uid));
    // mutateAsync rather than per-call callbacks: those only fire for the latest of
    // overlapping calls, and retries here can overlap (one per failed row).
    retryArtifact
      .mutateAsync({ projectId, file: sourceFile, selectedUids: [artifact.uid] })
      .then((retried) => recordArtefactEvents(projectId, "retry_artefact", retried))
      .catch(() => {
        // Surfaced via retryArtifact.isError.
      })
      .finally(() =>
        setRetryingUids((prev) => {
          const next = new Set(prev);
          next.delete(artifact.uid);
          return next;
        })
      );
  };

  const outstanding = [
    progress.working > 0 && `${progress.working} still being processed`,
    progress.failed > 0 && `${progress.failed} failed`,
  ].filter(Boolean) as string[];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[1320px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-lg font-bold">Upload CEM Model</DialogTitle>
          <DialogDescription>
            Check each description and correct anything that reads wrong — this text is what
            report writing searches for.
          </DialogDescription>
        </DialogHeader>

        <ErrorBanner
          message={
            retryArtifact.isError
              ? retryArtifact.error?.message || "Could not retry that artefact. Please try again."
              : null
          }
        />

        {progress.working > 0 && (
          <div className="flex items-center gap-2 rounded-md border border-blue-200 bg-blue-50 px-3 py-2 text-sm text-blue-700">
            <Loader2 className="w-4 h-4 shrink-0 animate-spin" />
            {progress.describedTotal > 0
              ? `Describing artefacts — ${progress.describedDone} of ${progress.describedTotal} done. Descriptions appear here as they finish.`
              : `Finishing up — ${progress.working} still to process.`}
          </div>
        )}

        {artifacts.length === 0 ? (
          <p className="text-sm text-muted-foreground py-8 text-center">
            No artefacts to review yet.
          </p>
        ) : (
          <ArtifactReviewList
            artifacts={artifacts}
            projectId={projectId}
            onRetry={handleRetry}
            retryingUids={retryingUids}
            latestUploadVersion={latestUploadVersion}
          />
        )}

        <div className="flex items-center justify-between gap-4 border-t pt-4">
          {/* Said rather than enforced. The artefacts are already on the
              project, so blocking here would only trap the user when the worker
              is down — which is exactly when they most need out. */}
          <p className="text-xs text-muted-foreground">
            {outstanding.length > 0
              ? `${outstanding.join(" and ")}. They're already added — you can check them on their cards.`
              : ""}
          </p>
          <Button
            type="button"
            variant="brand"
            className="shrink-0"
            onClick={() => onOpenChange(false)}
          >
            Continue
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ArtifactReviewDialog;
